/**
 * IndexCapacityHeatmapCell.tsx
 * Item 5.3 — one calendar day's cell in the shared Index-machine daily
 * utilization heatmap.
 *
 * Reuses `heatmapColorToken` from `CapacityHeatmapCell` (the existing weekly
 * employee-capacity cell) for the band→theme.schedule.heatmap* token mapping,
 * so the Index heatmap and the Employee Schedule heatmap read the same "0–70
 * green / 71–100 yellow / >100 red" color language (I9) rather than
 * reinventing a second band function.
 *
 * Soft (other drafts' Tentative, I13) is advisory — it never changes the
 * cell's fill color/band, only an outlined "+N%" secondary chip (mirrors the
 * conflictSoft/orange advisory language used across the scheduling views,
 * e.g. ConflictBadge/PlacementConfirmDialog) plus the tooltip breakdown.
 * Over-capacity (I9) adds a red inset ring + warning glyph — warn, never block.
 *
 * Item 5.4 — Compare-to-Baseline ghost (I16): `baselineGhost`, when supplied,
 * renders the BASELINE perspective's firmPct as a dashed ghost badge — the
 * SAME visual language `Phase1MachineGrid`'s ghost bars use (2px dashed/solid
 * border at 0.75 alpha, 0.18 alpha fill, 0.9 opacity, `theme.schedule.committed`
 * as the ghost color since baseline = already-committed data), translated from
 * an absolutely-positioned bar to a small badge since this cell is a %
 * value, not a Gantt row. Never forks the color language — same tokens.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { heatmapColorToken } from '../../../../../components/schedule/CapacityHeatmapCell';
import { buildDayTooltipLines, type IndexDayCellModel } from './indexHeatmapModel';
import type { DayBaselineGhost } from './indexBaselineDiff';

/** Shared cell width so the header row (IndexCapacityHeatmapGrid) lines up exactly. */
export const HEATMAP_CELL_WIDTH = 42;
export const HEATMAP_CELL_HEIGHT = 52;

export interface IndexCapacityHeatmapCellProps {
  model: IndexDayCellModel;
  /** Item 5.4 — BASELINE perspective for this date, or null/undefined when compare mode is off. */
  baselineGhost?: DayBaselineGhost | null;
}

function TooltipBody({
  model,
  ghost,
}: {
  model: IndexDayCellModel;
  ghost?: DayBaselineGhost | null;
}): React.JSX.Element {
  const lines = buildDayTooltipLines(model, ghost);
  return (
    <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>{model.date}</Typography>
      {lines.map((line, i) => (
        <Typography key={i} component="div" sx={{ fontSize: '0.7rem', whiteSpace: 'pre' }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export const IndexCapacityHeatmapCell: React.FC<IndexCapacityHeatmapCellProps> = ({
  model,
  baselineGhost,
}) => {
  const theme = useTheme();

  if (!model.isWorkingDay) {
    return (
      <Tooltip title={<TooltipBody model={model} ghost={baselineGhost} />} arrow placement="top">
        <Box
          role="gridcell"
          aria-label={`${model.date}: non-working day`}
          sx={{
            width: HEATMAP_CELL_WIDTH,
            height: HEATMAP_CELL_HEIGHT,
            flexShrink: 0,
            backgroundColor: alpha(theme.schedule.noActivity, 0.16),
            borderRight: `1px solid ${theme.border.color}`,
            // Row separator lives INSIDE the border-box cell height so every data
            // row is exactly HEATMAP_CELL_HEIGHT tall — matching the pinned label
            // column's rows. A wrapper-level border added 1px per row and the two
            // panes drifted apart further down the grid (2026-08-04).
            borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}`,
          }}
        />
      </Tooltip>
    );
  }

  const token = heatmapColorToken(model.firmPct);
  const bg = token === 'noActivity' ? 'transparent' : alpha(theme.schedule[token], 0.3);
  const fg = token === 'noActivity' ? theme.palette.text.disabled : theme.schedule[token];

  // Item 5.4 — only worth a ghost badge when the draft actually differs from
  // baseline; a zero delta would just clutter an already-dense cell.
  const showGhost = Boolean(baselineGhost) && baselineGhost!.deltaPct !== 0;

  return (
    <Tooltip title={<TooltipBody model={model} ghost={baselineGhost} />} arrow placement="top">
      <Box
        role="gridcell"
        aria-label={`${model.date}: ${model.firmPct}% firm${model.softPct > 0 ? `, +${model.softPct}% soft` : ''}${model.isOverCapacity ? ', over capacity' : ''}`}
        sx={{
          width: HEATMAP_CELL_WIDTH,
          height: HEATMAP_CELL_HEIGHT,
          flexShrink: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
          backgroundColor: bg,
          borderRight: `1px solid ${theme.border.color}`,
          // Inside the border-box height — keeps every data row exactly
          // HEATMAP_CELL_HEIGHT tall, aligned with the pinned label rows.
          borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}`,
          boxShadow: model.isOverCapacity
            ? `inset 0 0 0 2px ${theme.schedule.heatmapHigh}`
            : 'none',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ fontSize: '0.72rem', color: fg, lineHeight: 1.1 }}
        >
          {model.firmPct}%
        </Typography>

        {/* Soft/advisory secondary indicator (I13) — never affects the cell's fill/band. */}
        {model.softPct > 0 && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.56rem',
              fontWeight: 700,
              color: theme.schedule.conflictSoft,
              border: `1px dashed ${theme.schedule.conflictSoft}`,
              borderRadius: '3px',
              px: 0.3,
              lineHeight: 1.3,
            }}
          >
            +{model.softPct}%
          </Typography>
        )}

        {/* Item 5.4 — Compare-to-Baseline ghost badge. Same alpha/border language as
            Phase1MachineGrid's ghost bars (border 0.75 alpha / fill 0.18 alpha / 0.9
            opacity), translated to a small dashed badge for this % cell. Baseline is
            already-committed data, so it borrows `theme.schedule.committed` — the same
            color Phase1's ghost bars use for a WorkOrder/committed source. */}
        {showGhost && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.56rem',
              fontWeight: 700,
              color: alpha(theme.schedule.committed, 0.9),
              border: `1px dashed ${alpha(theme.schedule.committed, 0.75)}`,
              backgroundColor: alpha(theme.schedule.committed, 0.18),
              borderRadius: '3px',
              px: 0.3,
              lineHeight: 1.3,
            }}
          >
            base {baselineGhost!.baselineFirmPct}%
          </Typography>
        )}

        {model.isOverCapacity && (
          <WarningAmberIcon
            aria-label="Over capacity"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              fontSize: '0.72rem',
              color: theme.schedule.heatmapHigh,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};
